import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadMany, fileUrl } from "@/lib/uploads";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Camera, Upload, Mic, Square, Loader2, Trash2, Play } from "lucide-react";

/**
 * MediaInputRow — Feb 2026.
 * Reusable Capture-Photo + Upload-Photo + Voice-Note-with-Whisper row for
 * Execution Stage notes, Snag raising, Ticket raising, and Material Request
 * forms.
 *
 * Props:
 *   notes           — current notes string
 *   onNotesChange   — (str) => void ; voice transcript is APPENDED
 *   attachments     — [{id, url, kind, uploaded_by_user_id, uploaded_by_name}]
 *   onAttachmentsChange — full replacement setter
 *
 * The parent submits `attachments` alongside the record. Each attachment now
 * carries `uploaded_by_user_id` so downstream permission checks (only-uploader
 * or admin can delete) become trivial to enforce anywhere the row appears.
 */
export default function MediaInputRow({
  notes = "", onNotesChange,
  attachments = [], onAttachmentsChange,
  placeholder = "Add notes…", testidPrefix = "media",
}) {
  const { principal, isAdmin, roleType } = useAuth();
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRec = useRef(null);
  const chunks = useRef([]);

  const attachFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploaded = await uploadMany(Array.from(files));
      const stamped = uploaded.map((u) => ({
        ...u,
        kind: u.kind || (u.content_type?.startsWith("image/") ? "photo" : "file"),
        uploaded_by_user_id: principal?.id,
        uploaded_by_name: principal?.name || principal?.email,
        uploaded_at: new Date().toISOString(),
      }));
      onAttachmentsChange([...(attachments || []), ...stamped]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const canDelete = (att) => isAdmin || roleType === "director"
    || att.uploaded_by_user_id === principal?.id;

  const removeAttachment = (idx) => {
    const att = attachments[idx];
    if (!canDelete(att)) {
      toast.error("Only the uploader (or an admin) can delete this");
      return;
    }
    onAttachmentsChange(attachments.filter((_, i) => i !== idx));
  };

  // ── Voice recording via MediaRecorder ─────────────────────────────────
  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        await handleRecordedBlob(new Blob(chunks.current, { type: "audio/webm" }));
      };
      rec.start();
      mediaRec.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };
  const stopRecord = () => {
    if (mediaRec.current && recording) { mediaRec.current.stop(); setRecording(false); }
  };

  const handleRecordedBlob = async (blob) => {
    setTranscribing(true);
    try {
      // 1. Store the raw audio file so it can be played back later.
      const audioFile = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
      const [uploaded] = await uploadMany([audioFile]);
      // 2. Send to /voice/transcribe for STT + auto-translate to English.
      const fd = new FormData();
      fd.append("file", audioFile);
      const { data } = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const english = (data.text || "").trim();
      if (english) {
        // Append to existing notes on a new line
        onNotesChange((notes ? notes + "\n" : "") + english);
      }
      const stamped = {
        ...uploaded,
        kind: "voice",
        transcript_en: english,
        source_text: data.source_text || null,
        detected_language: data.detected_language || null,
        uploaded_by_user_id: principal?.id,
        uploaded_by_name: principal?.name || principal?.email,
        uploaded_at: new Date().toISOString(),
      };
      onAttachmentsChange([...(attachments || []), stamped]);
      toast.success(`Voice note transcribed (${data.detected_language || "auto"})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcription failed");
    } finally { setTranscribing(false); }
  };

  return (
    <div className="space-y-2" data-testid={testidPrefix}>
      <div className="relative">
        <textarea
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="iz-input w-full text-sm pr-12"
          data-testid={`${testidPrefix}-notes`}
        />
        <button type="button"
                onClick={recording ? stopRecord : startRecord}
                disabled={transcribing}
                title={recording ? "Stop recording" : "Voice note (Kn/Hi/Te/En)"}
                className={`absolute top-2 right-2 p-1.5 rounded border transition-colors ${
                  recording
                    ? "border-red-500/70 text-red-400 bg-red-500/10 animate-pulse"
                    : "border-[#262626] text-gray-400 hover:text-[#d4af37] hover:border-[#d4af37]/50"
                }`}
                data-testid={`${testidPrefix}-mic`}>
          {transcribing ? <Loader2 className="w-4 h-4 animate-spin"/>
           : recording ? <Square className="w-4 h-4"/>
           : <Mic className="w-4 h-4"/>}
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="iz-btn-secondary text-xs flex items-center gap-1.5"
                data-testid={`${testidPrefix}-upload-btn`}>
          <Upload className="w-3.5 h-3.5"/> Upload Photo
        </button>
        <button type="button" onClick={() => cameraInput.current?.click()}
                disabled={uploading}
                className="iz-btn-secondary text-xs flex items-center gap-1.5"
                data-testid={`${testidPrefix}-capture-btn`}>
          <Camera className="w-3.5 h-3.5"/> Capture Photo
        </button>
        {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400"/>}
        <input ref={fileInput} type="file" accept="image/*,application/pdf" multiple
               className="hidden"
               onChange={(e) => { attachFiles(e.target.files); e.target.value = ""; }}
               data-testid={`${testidPrefix}-upload-input`}/>
        <input ref={cameraInput} type="file" accept="image/*" capture="environment"
               className="hidden"
               onChange={(e) => { attachFiles(e.target.files); e.target.value = ""; }}
               data-testid={`${testidPrefix}-capture-input`}/>
      </div>

      {(attachments && attachments.length > 0) && (
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-2"
            data-testid={`${testidPrefix}-attachments`}>
          {attachments.map((att, i) => (
            <li key={att.id || i}
                className="relative border border-[#1a1a1a] rounded p-2 bg-[#0a0a0a] group"
                data-testid={`${testidPrefix}-att-${att.id || i}`}>
              {att.kind === "voice" ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
                    <Play className="w-3 h-3"/> Voice · {att.detected_language || "auto"}
                  </div>
                  <audio controls src={fileUrl(att.id)} className="w-full h-8"/>
                  {att.transcript_en && (
                    <p className="text-[10px] text-gray-500 line-clamp-2 italic">
                      "{att.transcript_en}"
                    </p>
                  )}
                </div>
              ) : (att.content_type || "").startsWith("image/") ? (
                <img src={fileUrl(att.id)} alt=""
                     className="w-full h-24 object-cover rounded"/>
              ) : (
                <a href={fileUrl(att.id)} target="_blank" rel="noreferrer"
                   className="text-xs text-gray-300 hover:text-[#d4af37] truncate block">
                  {att.filename || att.name || "attachment"}
                </a>
              )}
              <p className="text-[9px] text-gray-600 mt-1 truncate">
                {att.uploaded_by_name || "—"}
              </p>
              {canDelete(att) && (
                <button type="button" onClick={() => removeAttachment(i)}
                        title="Delete"
                        className="absolute top-1 right-1 p-1 rounded bg-black/70 text-gray-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`${testidPrefix}-delete-${att.id || i}`}>
                  <Trash2 className="w-3 h-3"/>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
